// Reports — download the people behind the figures as a PDF: ID holders
// under the filters (status, ID number, name, clan, family name, NRB area
// down to village, phone, registration date), listing ID, name, clan,
// family name, area and phone. One
// download holds at most EXPORT_LIMIT records; every download is logged.

import React, { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, Grid, InputBase, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useQuery } from '@tanstack/react-query';
import { EXPORT_LIMIT, exportApi, ExportFilters, ExportStatus } from '@services/api/analytics';
import { areaName, useDataScope } from './data';
import { emptyNrb, nrbLabel, NrbFilter, NrbLevel, NRB_LEVELS, normalizeNrb, nrbVisibleLevels, useNrbOptions } from './agg';
import { allDates, C, DateFilter, DateFilterValue, Dropdown, FilterBar, fmt, Kpi, Panel, Section, Toggle } from './ui';

const ALL: DateFilterValue = { preset: 'all', from: '', to: '' };
const STATUS_LABELS: Record<ExportStatus, string> = {
  unregistered: 'Unregistered ID holders', registered: 'Registered voters', all: 'All ID holders',
};
/** Diaspora and prisons voters: registered only, listed by mission / prison. */
const GROUPS = ['DIASPORA', 'PRISONS'];

const NrbDropdown: React.FC<{ level: NrbLevel; f: NrbFilter; set: (l: NrbLevel, v: string) => void }> = ({ level, f, set }) => {
  const { values, loading } = useNrbOptions(level, f);
  const i = NRB_LEVELS.indexOf(level);
  const ready = NRB_LEVELS.slice(0, i).every((l) => f[l]);
  const label = nrbLabel(level, f.county);
  return (
    <Dropdown label={label} value={f[level]} onChange={(v) => set(level, v)}
      options={values.map((v) => ({ value: v, label: areaName(v) }))}
      placeholder={!ready ? `Pick a ${nrbLabel(NRB_LEVELS[i - 1], f.county).toLowerCase()} first` : loading ? 'Loading…' : 'All'}
      disabled={!ready} />
  );
};

const ExtraDropdown: React.FC<{ level: 'sub_location' | 'village'; f: ExportFilters; onChange: (v: string) => void }> = ({ level, f, onChange }) => {
  const ready = level === 'sub_location' ? !!f.location : !!f.sub_location;
  const q = useQuery({
    queryKey: ['analytics', 'export-options', level, f.county, f.subcounty, f.division, f.location, level === 'village' ? f.sub_location : ''],
    queryFn: () => exportApi.options(level, f),
    enabled: ready,
    staleTime: 30 * 60 * 1000,
  });
  const label = level === 'sub_location' ? 'Sub-location' : 'Village';
  return (
    <Dropdown label={label} value={f[level]} onChange={onChange} options={ready ? q.data?.values ?? [] : []}
      placeholder={!ready ? `Pick a ${level === 'sub_location' ? 'location' : 'sub-location'} first` : q.isFetching ? 'Loading…' : 'All'}
      disabled={!ready} />
  );
};

/** A text filter that applies once typing pauses. */
const SearchField: React.FC<{ label: string; value: string; onChange: (v: string) => void; placeholder: string; disabled?: boolean; hint?: string }> = ({
  label, value, onChange, placeholder, disabled, hint,
}) => {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  useEffect(() => {
    const t = setTimeout(() => { if (text.trim() !== value) onChange(text.trim()); }, 600);
    return () => clearTimeout(t);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Box sx={{ minWidth: 170, flex: '1 1 auto', maxWidth: 240 }}>
      <Typography sx={{ fontSize: '0.62rem', color: C.muted, fontWeight: 700, letterSpacing: 0.5, mb: 0.4, textTransform: 'uppercase' }}>{label}</Typography>
      <InputBase value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} disabled={disabled} fullWidth
        sx={{ fontSize: '0.8rem', color: C.text, backgroundColor: C.bg, border: `1px solid ${C.border}`, borderRadius: 1, px: 1, height: 40, opacity: disabled ? 0.55 : 1 }} />
      {hint && <Typography sx={{ fontSize: '0.6rem', color: C.muted, mt: 0.25 }}>{hint}</Typography>}
    </Box>
  );
};

export const ReportsSection: React.FC<{ county: string }> = ({ county }) => {
  const scope = useDataScope();
  const [chosenStatus, setStatus] = useState<ExportStatus>('unregistered');
  const [area, setArea] = useState<NrbFilter>(normalizeNrb({ ...emptyNrb, county }));
  const [extra, setExtra] = useState({ sub_location: '', village: '' });
  const [dates, setDates] = useState<DateFilterValue>(ALL);
  const [withPhone, setWithPhone] = useState(false);
  const [search, setSearch] = useState({ id_number: '', name: '', phone: '', clan: '', family: '' });
  const [progress, setProgress] = useState<number | null>(null);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  React.useEffect(() => {
    setArea(normalizeNrb({ ...emptyNrb, county }));
    setExtra({ sub_location: '', village: '' });
  }, [county]);

  // Diaspora and prisons voters are all on the roll: registered only
  const isGroup = GROUPS.includes(area.county);
  const status = isGroup ? 'registered' : chosenStatus;
  const datesApply = status !== 'unregistered';
  const filters: ExportFilters = {
    status, ...area, ...extra,
    date_from: datesApply && dates.preset !== 'all' ? dates.from : '',
    date_to: datesApply && dates.preset !== 'all' ? dates.to : '',
    with_phone: withPhone,
    ...search,
  };
  const key = JSON.stringify(filters);
  const count = useQuery({ queryKey: ['analytics', 'export-count', key], queryFn: () => exportApi.count(filters), staleTime: 5 * 60 * 1000 });
  const preview = useQuery({
    queryKey: ['analytics', 'export-preview', key], queryFn: () => exportApi.preview(filters),
    enabled: !!area.county || !!search.id_number, staleTime: 5 * 60 * 1000,
  });

  const setLevel = (lvl: NrbLevel, v: string) => {
    const i = NRB_LEVELS.indexOf(lvl);
    const next = { ...area, [lvl]: v };
    NRB_LEVELS.slice(i + 1).forEach((l) => { next[l] = ''; });
    setArea(normalizeNrb(next));
    setExtra({ sub_location: '', village: '' });
    setResult(null);
  };
  const clear = () => {
    setArea(emptyNrb); setExtra({ sub_location: '', village: '' }); setDates(ALL); setWithPhone(false);
    setSearch({ id_number: '', name: '', phone: '', clan: '', family: '' }); setResult(null);
  };

  const n = count.data;
  const tooMany = !!n?.over_limit;
  const chosen = !!area.county || !!search.id_number;
  const canDownload = chosen && !!n && !tooMany && n.count > 0 && progress === null;
  const scopeName = [extra.village, extra.sub_location, area.location, area.division, area.subcounty, areaName(area.county)].find(Boolean) || 'Kenya';

  const download = async () => {
    setResult(null);
    setProgress(0);
    try {
      const { fileName, records } = await exportApi.download(filters, setProgress);
      setResult({ ok: true, text: `Saved ${fileName} · ${fmt(records)} records` });
    } catch (e: any) {
      setResult({ ok: false, text: e?.message || 'Download failed' });
    } finally {
      setProgress(null);
    }
  };

  return (
    <Section id="reports" icon={<AssessmentIcon />} title="Reports" accent={C.blue}
      subtitle={`${STATUS_LABELS[status]} · ${scopeName} · download the list as a PDF (at most ${fmt(EXPORT_LIMIT)} records at a time)`}>
      <Box sx={{ mb: 1.5 }}>
        <Toggle value={status} onChange={(v) => { setStatus(v as ExportStatus); setResult(null); }}
          options={(Object.keys(STATUS_LABELS) as ExportStatus[]).filter((s) => !isGroup || s === 'registered').map((s) => ({ value: s, label: STATUS_LABELS[s] }))} />
        {isGroup && (
          <Typography component="span" sx={{ fontSize: '0.72rem', color: C.muted, ml: 1.5 }}>
            {areaName(area.county)} voters are all registered (on the IEBC roll).
          </Typography>
        )}
      </Box>
      <FilterBar onClear={clear}>
        <SearchField label="ID number" value={search.id_number} placeholder="e.g. 30984084"
          onChange={(v) => { setSearch({ ...search, id_number: v }); setResult(null); }} />
        <SearchField label="Name" value={search.name} placeholder="Part of the name" disabled={!area.county}
          hint={!area.county ? 'Pick a county first' : undefined}
          onChange={(v) => { setSearch({ ...search, name: v }); setResult(null); }} />
        <SearchField label="Clan" value={search.clan} placeholder="Part of the clan" disabled={!area.county}
          hint={!area.county ? 'Pick a county first' : undefined}
          onChange={(v) => { setSearch({ ...search, clan: v }); setResult(null); }} />
        <SearchField label="Family name" value={search.family} placeholder="Part of the family name" disabled={!area.county}
          hint={!area.county ? 'Pick a county first' : undefined}
          onChange={(v) => { setSearch({ ...search, family: v }); setResult(null); }} />
        {NRB_LEVELS.filter((l) => nrbVisibleLevels(area.county).includes(l)).map((l) => <NrbDropdown key={l} level={l} f={area} set={setLevel} />)}
        {!isGroup && <>
          <ExtraDropdown level="sub_location" f={filters} onChange={(v) => { setExtra({ sub_location: v, village: '' }); setResult(null); }} />
          <ExtraDropdown level="village" f={filters} onChange={(v) => { setExtra({ ...extra, village: v }); setResult(null); }} />
        </>}
        <SearchField label="Phone number" value={search.phone} placeholder="Part of the number" disabled={!area.county}
          hint={!area.county ? 'Pick a county first' : undefined}
          onChange={(v) => { setSearch({ ...search, phone: v.replace(/[^\d+]/g, '') }); setResult(null); }} />
        {datesApply && scope.first && (
          <DateFilter label="Date of registration" value={dates.preset === 'all' ? allDates(scope.first, scope.last) : dates}
            onChange={setDates} first={scope.first} last={scope.last} />
        )}
        <FormControlLabel sx={{ mt: 2, color: C.text, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
          control={<Checkbox size="small" checked={withPhone} onChange={(e) => setWithPhone(e.target.checked)} sx={{ color: C.muted, '&.Mui-checked': { color: C.green } }} />}
          label="Only people with a phone number" />
      </FilterBar>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Kpi label="Records matching" color={tooMany ? C.orange : C.blue}
            value={!chosen ? '—' : count.isFetching ? '…' : tooMany ? `Over ${fmt(EXPORT_LIMIT)}` : n ? n.count : '—'}
            sub={!chosen ? 'Pick a county (or enter an ID number) first' : `${STATUS_LABELS[status].toLowerCase()} · ${scopeName}`} />
        </Grid>
        <Grid item xs={12} md={8}>
          <Panel sx={{ height: '100%' }}>
            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, height: '100%', justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button variant="contained" disabled={!canDownload} onClick={download}
                  startIcon={progress !== null ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
                  sx={{ backgroundColor: C.blue, fontWeight: 700, '&:hover': { backgroundColor: C.blue, filter: 'brightness(1.1)' } }}>
                  {progress !== null ? 'Preparing PDF…' : n && !tooMany && chosen ? `Download ${fmt(n.count)} records (PDF)` : 'Download PDF'}
                </Button>
                <Typography sx={{ fontSize: '0.72rem', color: C.muted }}>
                  {progress !== null
                    ? `${(progress / 1e6).toFixed(1)} MB received… large files take a few minutes`
                    : `About ${fmt(Math.max(1, Math.ceil((n?.count ?? 0) / 47)))} pages. Every download is recorded with your name.`}
                </Typography>
              </Box>
              {progress !== null && <LinearProgress sx={{ height: 3, backgroundColor: C.faint, '& .MuiLinearProgress-bar': { backgroundColor: C.blue } }} />}
              {!chosen && <Typography sx={{ fontSize: '0.78rem', color: C.muted }}>Pick a county to download. A whole-country list is far more than {fmt(EXPORT_LIMIT)} records.</Typography>}
              {tooMany && area.county && (
                <Typography sx={{ fontSize: '0.78rem', color: C.orange }}>
                  More than {fmt(EXPORT_LIMIT)} records match. Narrow the filters (pick a {nrbLabel('subcounty', area.county).toLowerCase()}, or a shorter date range) and download it in parts.
                </Typography>
              )}
              {result && <Typography sx={{ fontSize: '0.78rem', color: result.ok ? C.green : C.orange }}>{result.text}</Typography>}
              {count.error && <Typography sx={{ fontSize: '0.78rem', color: C.orange }}>{(count.error as any)?.error?.message || 'Could not count the records.'}</Typography>}
            </Box>
          </Panel>
        </Grid>
      </Grid>

      <Panel title={<>Preview · first {preview.data?.rows.length ?? 20} records</>} sx={{ height: 'auto' }}
        actions={<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {(preview.data?.columns ?? []).map((c) => <Chip key={c} label={c} size="small" sx={{ fontSize: '0.62rem', height: 20, color: C.muted, backgroundColor: C.faint }} />)}
        </Box>}>
        {preview.isFetching && <LinearProgress sx={{ height: 2, backgroundColor: 'transparent', '& .MuiLinearProgress-bar': { backgroundColor: C.blue } }} />}
        {!chosen ? (
          <Typography sx={{ p: 2, fontSize: '0.8rem', color: C.muted }}>Pick a county (or enter an ID number) to preview the records.</Typography>
        ) : preview.error ? (
          <Typography sx={{ p: 2, fontSize: '0.8rem', color: C.orange }}>{(preview.error as any)?.error?.message || 'Could not load the preview.'}</Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {(preview.data?.columns ?? []).map((c) => (
                    <TableCell key={c} sx={{ backgroundColor: C.cardAlt, color: C.muted, fontSize: '0.64rem', fontWeight: 700, whiteSpace: 'nowrap', borderBottom: `1px solid ${C.border}` }}>
                      {c.toUpperCase()}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(preview.data?.rows ?? []).map((r) => (
                  <TableRow key={r[0]} hover>
                    {r.map((v, j) => (
                      <TableCell key={j} sx={{ color: C.text, fontSize: '0.74rem', whiteSpace: 'nowrap', borderBottom: `1px solid ${C.border}`, py: 0.5 }}>{v || '—'}</TableCell>
                    ))}
                  </TableRow>
                ))}
                {preview.data && preview.data.rows.length === 0 && (
                  <TableRow><TableCell colSpan={13} sx={{ color: C.muted, fontSize: '0.8rem', borderBottom: 'none' }}>No records match these filters.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Panel>
      <Typography sx={{ fontSize: '0.68rem', color: C.muted, mt: 1 }}>
        From NRB records and the IEBC voter roll. Unregistered = ID holders not on the roll. Registration dates apply to registered voters only.
        Diaspora and Prisons list registered voters by mission or prison; their sub-location, village and phone come from their home NRB record.
        Outside Diaspora and Prisons, registered voters with no NRB record are not listed (they have no NRB details).
      </Typography>
    </Section>
  );
};
